import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import {
  createNewOrder,
  createPilotOrder,
  getDeliveryCharge,
  getTimeSlots,
  logAction,
} from "../services/api";
import { CommonContext } from "../contexts/CommonContext";
import { getFirebaseError } from "../services/error-codes";
import ComponentLoader from "../components/ComponentLoader";
import ItemsSummary from "../components/ItemsSummary";
import CartSuggestions from "../components/CartSuggestions";
import FreeDeliveryPromotion from "../components/FreeDeliveryPromotion";
import NavHeader from "../components/NavHeader";
import { Capacitor } from "@capacitor/core";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import { trackMetaPixelEvent, metaPixelUserParams, setMetaPixelUserData } from "../utils/metaPixel";

const styles = {
  cartCont: {
    display: "flex",
    padding: "20px",
    alignItems: "center",
  },
  cartImg: {
    width: "100px",
    height: "100px",
    borderRadius: "20px",
  },
  shadowBox: {
    background: "white",
    // borderRadius : '13px',
    boxShadow: "0px 0px 10px 0px #eaeaea",
  },
};

function OrderSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserId, getCustomerId, logout } = useContext(AuthContext);
  const {
    showLoader,
    hideLoader,
    showAlert,
    showSnackbar,
    couponCacheData,
    cartData,
  } = useContext(CommonContext);
  const [loading, setLoading] = useState(true);
  const [deliveryCharge, setDeliveryCharge] = useState({ charge: 0, distance: null });
const [userId,setUserId]=useState('')

const phoneNum = async () => {
  setUserId(await getUserId())
}

  // Use cartData from context when it has items (updates when user adds from CartSuggestions)
  const CART_META_KEYS = ['totalAmount', 'totalCount', 'totalDiscount', 'totalBasePrice', 'totalExtras', 'bogoDiscount'];
  const hasCartProducts = cartData && Object.keys(cartData).filter(k => !CART_META_KEYS.includes(k)).length > 0;
  const displayItemDetails = hasCartProducts ? cartData : (location.state?.itemDetails || {});

  useEffect(() => {
    fetchDeliveryCharge();
    phoneNum()
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  // Recalculate delivery charge when cart changes (e.g. user adds from CartSuggestions for free delivery)
  useEffect(() => {
    if (!location.state?.storeDetails || displayItemDetails?.totalAmount == null) return;
    // Use pre-calculated charge only when cart total hasn't changed (user hasn't added from suggestions)
    const totalUnchanged = displayItemDetails?.totalAmount === location.state?.itemDetails?.totalAmount;
    if (totalUnchanged && location.state?.deliveryCharge && typeof location.state.deliveryCharge.charge === "number") {
      setDeliveryCharge(location.state.deliveryCharge);
      return;
    }
    if (totalUnchanged && typeof location.state?.deliveryCharge === "number") {
      setDeliveryCharge({ charge: location.state.deliveryCharge, distance: null });
      return;
    }
    getDeliveryCharge(
      location.state.delType,
      location.state.storeDetails,
      displayItemDetails.totalAmount,
      location.state.customerLat,
      location.state.customerLng
    ).then(charge => setDeliveryCharge({ charge, distance: null }));
  }, [displayItemDetails?.totalAmount]);

  const fetchDeliveryCharge = async () => {
    // Use pre-calculated dynamic delivery charge from Delivery.js when available
    if (location.state?.deliveryCharge && typeof location.state.deliveryCharge.charge === "number") {
      setDeliveryCharge(location.state.deliveryCharge);
      return;
    }
    if (typeof location.state?.deliveryCharge === "number") {
      setDeliveryCharge({ charge: location.state.deliveryCharge, distance: null });
      return;
    }
    const calculatedDeliveryCharge = await getDeliveryCharge(
      location.state.delType,
      location.state.storeDetails,
      location.state.itemDetails.totalAmount,
      location.state.customerLat,
      location.state.customerLng
    );
    setDeliveryCharge({ charge: calculatedDeliveryCharge, distance: null });
  };

  // Separate function to handle pilot order creation for parallel testing
  const handlePilotOrderCreation = (orderData) => {

    try {
      // Build Pilot order data in the required format

      let cutMap = {
        "small" : "SCT", // Small Cut
        "medium" : "MCT", // Medium Cut
        "biryani" : "BCT", // Biryani Cut 
      }

      let boneMap = {
        "withBones" : "WITHBONE",
        "boneless" : "BONELESS"
      }

      let skinMap = {
        "withskin" : "WITHSKIN",
        "skinless" : "SKINLESS"
      }

      let flavourMap = {
        "normal" : "NML",
        "smoketurmeric" : "SMT"
      }
      
      orderData.itemDetails.forEach(item => {
       
        if (!item.customizations || Object.keys(item.customizations).length == 0) return
        
        //Handling Cut Type
        if (item.customizations?.piece_cut_size?.options) {
          item.customizations.piece_cut_size.options.forEach((option, index) => {
            if (option.sku == cutMap[item.extras.cutType]) {
              item.customizations.piece_cut_size = option
            }
          })
        }

        //Handling Skin Type
        if (item.customizations?.type_of_meat?.options) {
          item.customizations.type_of_meat.options.forEach((option, index) => {
            //Better than broiler
            if (item.id == 'C068') {
              if (option.sku == 'SKINLESS') {
                item.customizations.type_of_meat = option
              }
            } else {
              if (option.sku == skinMap[item.extras.skinType]) {
                item.customizations.type_of_meat = option
              }
            }
          })
        }

        //Handling Bone Type
        if (item.customizations?.bone_type?.options) {
          item.customizations.bone_type.options.forEach((option, index) => {
            if (option.sku == boneMap[item.extras.boneType]) {
              item.customizations.bone_type = option
            }
          })
        }

        //Handling Flavour Type
        if (item.customizations?.flavour?.options) {
          item.customizations.flavour.options.forEach((option, index) => {
            if (option.sku == flavourMap[item.extras.flavourType]) {
              item.customizations.flavour = option
            }
          })
        }

        //Handling Additional Customizations
        if (item.customizations?.additional_customization?.options) {
          if (item.extras.noNeck || item.extras.noLiverGizzard) {
            item.customizations.additional_customization.selections = []
          }
          item.customizations.additional_customization.options.forEach((option, index) => {
            if (item.extras.noNeck && option.sku == 'NN') {
              item.customizations.additional_customization.selections.push(option)
            }
            if (item.extras.noLiverGizzard && option.sku == 'LG') {
              item.customizations.additional_customization.selections.push(option)
            }
          })
          if (item.customizations.additional_customization.options) 
            delete item.customizations.additional_customization.options
        }
      })
   


      const pilotOrderData = {
        userId: orderData.userId,
        addressId: orderData.addressDetails?.addressId || null,
        deliveryAddress: {
          userName: orderData.addressDetails?.userName || '',
          houseDetails: orderData.addressDetails?.houseDetails || '',
          streetDetails: orderData.addressDetails?.streetDetails || '',
          landmark: orderData.addressDetails?.landmark || '',
          pincode: orderData.addressDetails?.pincode || '',
          latLong: orderData.addressDetails?.latLong || null
        },
        storeId: orderData.storeDetails?.storeId || orderData.storeDetails?.branchId || null,
        storeDetails: orderData.storeDetails || null,
        timeSlotId: location.state.timeSlotId,
        timeSlotDetails: location.state.timeSlotDetails,
        products: orderData.itemDetails?.map(item => ({
          productId: item.variant?.skuId,
          name: item.name,
          imageUrl: item.imageUrl || item.imgUrls?.[0] || null,
          quantity: item.count || 1,
          price: item.price,
          variant: item.variant || (item.variants && item.variants[0]) || {},
          customizations: item.customizations || {}
        })) || [],
        subtotal: parseFloat(orderData.totalAmount || 0),
        shippingCost: parseFloat(orderData.shippingCost || 0),
        discount: parseFloat(orderData.totalDiscount || 0),
        
        total: parseFloat(orderData.totalAmount + (orderData.shippingCost || 0) - (orderData.totalDiscount || 0)),
        appliedCoupon: orderData.couponCode || null,
        instructions: orderData.instructions?.trim() || null,
        deliveryDate: orderData.deliveryDate == 'Today' ? new Date().toISOString().split('T')[0] : orderData.deliveryDate == 'Tomorrow' ? new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : orderData.deliveryDate || null,
        deliveryDateTimestamp: orderData.timeStamp || null,
        branch_id: orderData.storeDetails?.branchId || null,
        status: 'pending',
        orderType: 'customer_order',
        createdBy: 'customer',
        paymentStatus : 'pending',

        //For Prana
        couponDiscountAmount: parseFloat(orderData.totalDiscount) || null,
        pranaCustomerId: orderData.customerId,
        pranaSlotId: location.state.delSlotId,
      };
      console.log(pilotOrderData)
      //Validating order data for now
      createPilotOrder(pilotOrderData).catch(error => {
        console.log('Pilot order failed (this is for testing purposes only):', error);
        // Log additional details for debugging
        console.log('Original order data:', orderData);
        console.log('Pilot order data:', pilotOrderData);
        console.log('Error details:', error.message || error);
      });
    } catch (error) {
      console.log('Error in handlePilotOrderCreation:', error);
    }
  };

  
  const placeOrder = async () => {
    
    window.scrollTo(0, 0);

    // const baseDeliveryCharge = deliveryCharge;
     
    // const totalDeliveryCharge = location.state.paymentMode === "cod" 
    // ? baseDeliveryCharge + 20 
    // : baseDeliveryCharge;


    const itemDetailsForOrder = displayItemDetails;
    let orderData = {
      userId: await getUserId(),
      timeStamp: Date.now(),
      status: "pending",
      addressDetails: location.state.addressDetails,
      totalCount: itemDetailsForOrder.totalCount,
      totalAmount: itemDetailsForOrder.totalAmount,
      paymentMode: location.state.paymentMode,
      customerId: await getCustomerId(),
      deliveryDate: location.state.delDate,
      deliverySlot: location.state.delSlotId,
      totalDiscount: location.state.totalDiscount,
      storeDetails: location.state.storeDetails,
      instructions: location.state.instructions,
      deliveryType: location.state.delType,
      shippingCost: deliveryCharge.charge,
      platform: Capacitor.getPlatform(),
      timeSlotId: location.state.timeSlotId,
      timeSlotDetails: location.state.timeSlotDetails,
      podName: location.state.storeDetails?.podName || null,
    };

    if (itemDetailsForOrder?.bogoDiscount) {
      orderData.couponCode = "BOGO";
      orderData.couponDiscountAmount = itemDetailsForOrder.bogoDiscount;
      orderData.couponDiscountType = "discount_coupon";
    }

    if (couponCacheData?.couponCode) {
      orderData.couponCode = couponCacheData.couponCode;
      orderData.couponDiscountAmount = couponCacheData.couponValue;
      orderData.couponDiscountType = "discount_coupon";
    }

    let ordersObj = JSON.parse(JSON.stringify(itemDetailsForOrder));
    delete ordersObj.totalAmount;
    delete ordersObj.totalCount;
    delete ordersObj.totalDiscount;
    delete ordersObj.bogoDiscount;
    delete ordersObj.totalBasePrice
    delete ordersObj.totalExtras

    orderData.orderTitle = ordersObj[Object.keys(ordersObj)[0]].name;

    orderData.itemDetails = Object.values(ordersObj);

    orderData.itemDetails.forEach((item) => {
      logAction("PLACE_ORDER", item.urlId);
    });
    showLoader();

    const purchaseValue =
      itemDetailsForOrder.totalAmount +
      (deliveryCharge?.charge ?? deliveryCharge) -
      (couponCacheData?.couponValue || 0);
    logEvent(analytics, "purchase", {
      transaction_id: Date.now().toString(),
      affiliation: Capacitor.getPlatform(),
      currency: "INR",
      value: purchaseValue,
      tax: 0,
      shipping: 0,
    });
    const userName = location.state.addressDetails?.userName || "";
    const nameParts = userName.trim().split(/\s+/);
    const userParams = metaPixelUserParams({
      phone: orderData.userId,
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "",
      externalId: orderData.customerId,
    });
    setMetaPixelUserData(userParams);
    trackMetaPixelEvent("Purchase", {
      value: purchaseValue,
      currency: "INR",
    });



    createNewOrder(orderData)
      .then((response) => {
        // location.state.storeDetails.branchId == 11
        // if (true) {
        //   //Creating pilot order for Chandanagar Store
        //   handlePilotOrderCreation(orderData);
        // }

        // Continue with existing flow
        navigate("/orderStatus", {
          state: { ...response, orderData: orderData },
          replace: true,
        });
        hideLoader();
      })
      .catch((error) => {
        // hideLoader()
        // showAlert(getFirebaseError(error.errorCode))
        // logout()
        // showAlert("Unexpected error occured. For any issues/enquiries please contact customer care.")
      });
  };

  return (
    <Box sx={{ padding: "4vw", maxWidth: "600px", marginTop: "7vh" }}>
      {loading ? (
        <ComponentLoader />
      ) : (
        <Box>
          <NavHeader />
          <Box
            sx={{
              fontSize: "30px",
              marginBottom: "10px",
              color: "#404040",
              fontFamily: "Foregen",
              textDecoration: "underline",
            }}
          >
            Order Summary
          </Box>

          <Box
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              marginBottom: "10px",
              fontFamily:'RobotoSlab'
            }}
          >
            Your order will be delivered from{" "}
            <span
              style={{
                color: "#A4243D", // Replace with your desired color
                fontWeight: "600", // Optional, for emphasis
              }}
            >
              {location.state.storeDetails.storeName}
            </span>{" "}
            store
          </Box>

          <Box
            sx={{
              mb: 1,
              fontSize: "15px",
              fontFamily: "Roboto",
              color: "#000",
              mt: 3,
              fontFamily:'RobotoSlabBold'
            }}
          >
            Bill Summary
          </Box>
          <Box sx={{ border: "3px solid #A4243D" }}>
            <ItemsSummary itemDetails={displayItemDetails} delType={location.state.delType} storeDetails={location.state.storeDetails}
             customerLat={location.state.addressDetails?.latLong?.lat} customerLng={location.state.addressDetails?.latLong?.lng}
             deliveryCharge={deliveryCharge} />
          </Box>
          <FreeDeliveryPromotion cartData={displayItemDetails} />
          <CartSuggestions cartData={displayItemDetails} />
          {/* <Box sx={{mb:1, fontSize:'20px', fontFamily:'Foregen', color:'#a4243d', mt:3}}>
          DELIVERING TO
        </Box> */}
          {/* <Box sx={{padding:'15px', color:'#404040', background:'#FFF5E8', boxShadow:'0px 0px 15px rgba(0, 0, 0, 0.15)', borderRadius:'5px'}}>
          <Box>

            <Box sx={{marginBottom:'5px', fontWeight:'bold'}}>
              {location.state.addressDetails.userName}
            </Box>
            <Box SX={{fontSize:'15px'}}>
              {location.state.addressDetails.houseDetails}, {location.state.addressDetails.streetDetails},
              {location.state.addressDetails.landmark}, {location.state.addressDetails.pincode}
            </Box>

          </Box>
        </Box>   */}

          <Box>
            <Box
              sx={{
                mb: 1,
                fontSize: "15px",
                fontFamily: "RobotoSlabBold",
                color: "#000",
                mt: 3,
              }}
            >
              Delivery Slot & Address
            </Box>

            <Box
              sx={{
                border: "1px solid #000",
                borderRadius: "7px",
                marginBottom: "20px",
               paddingLeft:'10px'
              }}
            >
              <Box
                sx={{
                  padding: "5px 8px 2px 8px",
                  borderRadius: "5px",
                  color: "#a4243d",
                  fontSize:'13px',
                  fontFamily:"RobotoSlab"
                }}
              >
                {location.state.delDate} , {location.state.delSlotTime},
              </Box>
              <Box sx={{ padding: "5px 8px 2px 8px", borderRadius: "5px" }}>
                <Box
                  sx={{
                    display: "flex",
                 
                  }}
                >
                  <Box
                    sx={{
                      marginBottom: "5px",
                      fontWeight: "700",
                      fontSize: "15px",
                      fontFamily: "RobotoSlabBold",
                    }}
                  >
                    {location.state.addressDetails.userName}
                  </Box>
                  <Box
                    sx={{
                      marginLeft: "5px",
                      fontWeight: "400",
                      fontSize: "12px",
                      fontFamily: "RobotoSlab",
                      alignSelf:'center'
                    }}
                  >
                    +91 {userId}
                  </Box>
                </Box>
                <Box
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    fontFamily: "RobotoSlab",
                    marginBottom:'10px'
                  }}
                >
                  {location.state.addressDetails.houseDetails},{" "}
                  {location.state.addressDetails.streetDetails},
                  {location.state.addressDetails.landmark},{" "}
                  {location.state.addressDetails.pincode}
                </Box>
              </Box>
              <Box>
                {location.state.instructions ? (
                  <>
                    <Box sx={{ marginBottom: "5px", marginTop: "5px", wordWrap: "break-word",padding:'0 10px' }}>
                      {location.state.instructions}
                    </Box>
                  </>
                ) : null}
              </Box>
            </Box>
           
          </Box>
          {/* <Box sx={{mb:1, fontSize:'20px', fontFamily:'Foregen', color:'#a4243d', mt:3}}>
          BILL DETAILS
        </Box>
        <Box sx={{padding:'15px', mb:4, background:'#FFF5E8', boxShadow:'0px 0px 15px rgba(0, 0, 0, 0.15)', borderRadius:'5px'}}> 
          <Box sx={{display:'flex', justifyContent:'space-between', color:'#404040'}}>
            <Box>
              Item Total 
            </Box>
            <Box>
              ₹ {Number(location.state.itemDetails.totalAmount) +  Number(location.state.itemDetails.totalDiscount) + Number(location.state.itemDetails.bogoDiscount)}
            </Box>
          </Box>

          {
            location.state.itemDetails?.bogoDiscount ? 
              <Box sx={{display:'flex', justifyContent:'space-between', padding:'5px 0', color:'#404040'}}>
                <Box>
                  BOGO Discount
                </Box>
                <Box sx={{color:'#a4243d'}}>
                  -₹{location.state.itemDetails.bogoDiscount}
                </Box>
              </Box> : null
          }

          {
            location.state.itemDetails?.totalDiscount ? 
              <Box sx={{display:'flex', justifyContent:'space-between', padding:'5px 0', color:'#404040'}}>
                <Box>
                  Discount on MRP
                </Box>
                <Box sx={{color:'#a4243d'}}>
                  -₹{location.state.itemDetails.totalDiscount}
                </Box>
              </Box> : null
          }
          

          {
            couponCacheData && couponCacheData.couponCode ?
            <Box sx={{display:'flex', justifyContent:'space-between', padding:'5px 0', color:'#404040'}}>
              <Box>
                Coupon Discount [ {couponCacheData.couponCode} ]
              </Box>
              <Box sx={{color:'#a4243d'}}>
              - ₹ {couponCacheData.couponValue}
              </Box>
            </Box>
            : null
          }

          <Box sx={{display:'flex', justifyContent:'space-between', padding:'5px 0', color:'#404040'}}>
            <Box>
              {location.state.delType == 'self_pickup' ? 'Handling Charges' : 'Delivery Fee'}
            </Box>
            <Box>
              ₹ {deliveryCharge.charge} 
            </Box>
          </Box>

          <Box sx={{display:'flex', justifyContent:'space-between',  borderTop:'1px solid #eaeaea', padding:'5px 0'}}>
            <Box>
              To Pay
            </Box>
            <Box sx={{fontWeight:'bold'}}>
              ₹ {location.state.itemDetails.totalAmount 
                + (deliveryCharge.charge)
                - (couponCacheData?.couponValue || 0)}
            </Box>
          </Box>
        </Box>  */}
        </Box>
      )}
       <Box
              sx={{
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                bottom: 0, 
                left: 0, 
                right: 0, 
               marginBottom:'10px',
               fontFamily:'Roboto',
               fontSize:'14px',
               fontWeight:'600'
              }}
            >
              {location.state.paymentMode === "online" ? (
                <Button
                  fullWidth
                  sx={{marginTop:'20px'}}
                  onClick={() =>
                    navigate(`/makePayment`, {
                      state: { ...location.state, itemDetails: displayItemDetails, deliveryCharge },
                      replace: true,
                    })
                  }
                  variant="contained"
                >
                  ✅ Place Order Now
                </Button>
              ) : (
                <Box display='flex' flexDirection='column' alignItems='center'>

                <Button sx={{marginTop:'20px',fontSize:'20px', marginBottom:'10px'}} 
                onClick={() => placeOrder()} variant="contained" size="large">
                  ✅ Place Order Now
                </Button>
                <Box sx={{fontSize:'14px', fontFamily:'RobotoSlab', color:'#5d5d5d'}}>
                  100% Fresh & Naturally Raised Meats
                </Box>
                </Box>
                
              )}
            </Box>
    </Box>
  );
}

export default OrderSummary;
